import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { chatAPI } from '../services/api';
import { ChatActivityUser, ChatActivityStats, ChatMessage, APIError } from '../types';

const ChatMonitoring: React.FC = () => {
  // Activity list state
  const [users, setUsers] = useState<ChatActivityUser[]>([]);
  const [stats, setStats] = useState<ChatActivityStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // Committed search term — see UserManagement: avoids racing a stale-page
  // request against the page-1 refetch when searching from page > 1
  const [activeSearch, setActiveSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);

  // Detail (conversation) state
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    username: string;
    email: string;
    phone_number?: string;
  } | null>(null);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState<any>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeSearch]);

  // Deep link from the dashboard's "Most Active Students" list:
  // /admin/chats?student=<id> opens that student's conversation directly.
  useEffect(() => {
    const studentId = parseInt(searchParams.get('student') || '', 10);
    if (!Number.isNaN(studentId)) {
      fetchHistory(studentId, 1);
      // Consume the param so Back doesn't re-trigger it
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await chatAPI.getAdminActivity(currentPage, 20, activeSearch);
      setUsers(response.users);
      setStats(response.stats);
      setPagination(response.pagination);
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to fetch chat activity');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (userId: number, page: number) => {
    try {
      setHistoryLoading(true);
      setHistoryError('');
      const response = await chatAPI.getAdminUserHistory(userId, page, 20);
      setSelectedUser(response.user);
      setHistory(response.history);
      setHistoryPagination(response.pagination);
      setHistoryPage(page);
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setHistoryError(apiError?.error || 'Failed to fetch chat history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Batched into one render → a single fetch with page 1 and the new term
    setActiveSearch(search);
    setCurrentPage(1);
  };

  const handleViewChats = (user: ChatActivityUser) => {
    setSelectedUser({
      id: user.id,
      username: user.username,
      email: user.email,
      phone_number: user.phone_number,
    });
    setHistory([]);
    setHistoryPagination(null);
    fetchHistory(user.id, 1);
  };

  const handleBack = () => {
    setSelectedUser(null);
    setHistory([]);
    setHistoryPagination(null);
    setHistoryError('');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statCards = [
    {
      label: 'Questions Today',
      value: stats?.questions_today ?? 0,
      iconBg: 'bg-bmlc-gold-100',
      iconColor: 'text-bmlc-gold-600',
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      ),
    },
    {
      label: 'Active Students Today',
      value: stats?.active_users_today ?? 0,
      iconBg: 'bg-bmlc-navy-100',
      iconColor: 'text-bmlc-navy-600',
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      ),
    },
    {
      label: 'Total Questions',
      value: stats?.total_questions ?? 0,
      iconBg: 'bg-bmlc-maroon-50',
      iconColor: 'text-bmlc-maroon-500',
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      ),
    },
  ];

  // ---------- Detail (conversation) view ----------
  if (selectedUser) {
    return (
      <div className="px-3 sm:px-4 py-4 sm:py-6 animate-fade-in">
        {/* Header with back button */}
        <div className="card-premium p-4 sm:p-5 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={handleBack}
              className="btn-ghost p-2 sm:p-2.5 rounded-lg flex-shrink-0"
              title="Back to activity"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div className="w-9 h-9 sm:w-11 sm:h-11 bg-bmlc-gold-50 ring-2 ring-bmlc-gold-300/70 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs sm:text-base font-serif font-bold text-bmlc-gold-700">
                {selectedUser.username?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="min-w-0">
              <h2 className="font-serif text-lg sm:text-xl font-bold text-bmlc-navy-900 truncate">{selectedUser.username}</h2>
              <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                {selectedUser.email}
                {selectedUser.phone_number ? ` • ${selectedUser.phone_number}` : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Conversation panel */}
        <div className="card p-4 sm:p-6">
          {historyError && (
            <div className="toast-error mb-3 sm:mb-4">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-xs sm:text-sm">{historyError}</p>
            </div>
          )}

          {historyLoading ? (
            <div className="text-center py-10 sm:py-14">
              <div className="loading-dots justify-center">
                <span className="loading-dot"></span>
                <span className="loading-dot"></span>
                <span className="loading-dot"></span>
              </div>
              <p className="text-gray-500 text-xs sm:text-sm mt-4">Loading conversations...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-10 sm:py-14">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-bmlc-gold-50 ring-1 ring-bmlc-gold-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-bmlc-gold-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="font-serif text-bmlc-navy-800 text-base sm:text-lg font-semibold mb-1">No conversations yet</p>
              <p className="text-gray-400 text-xs sm:text-sm">This student has not asked any questions</p>
            </div>
          ) : (
            <>
              <div className="space-y-5 sm:space-y-7">
                {history.map((item) => (
                  <div key={item.id} className="space-y-2.5 sm:space-y-3">
                    {/* Question bubble */}
                    <div className="flex justify-end">
                      <div className="max-w-[85%] sm:max-w-[75%] bg-navy-sheen text-white border border-bmlc-gold-500/20 shadow-soft px-3.5 sm:px-4 md:px-5 py-2.5 sm:py-3 rounded-2xl rounded-br-md">
                        <p className="whitespace-pre-wrap leading-relaxed text-xs sm:text-sm">{item.question}</p>
                        <p className="text-[10px] sm:text-[11px] mt-1.5 sm:mt-2 flex items-center space-x-1 text-bmlc-navy-100/80">
                          <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{formatDate(item.created_at)}</span>
                        </p>
                      </div>
                    </div>
                    {/* Answer bubble */}
                    <div className="flex justify-start">
                      <div className="max-w-[85%] sm:max-w-[75%] bg-white text-gray-800 border border-bmlc-navy-100 border-l-4 border-l-bmlc-gold-400 shadow-soft-sm px-3.5 sm:px-4 md:px-5 py-2.5 sm:py-3.5 rounded-2xl rounded-tl-md">
                        <p className="whitespace-pre-wrap leading-relaxed sm:leading-7 text-xs sm:text-sm">{item.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {historyPagination && historyPagination.total_pages > 1 && (
                <div className="flex items-center justify-between mt-5 sm:mt-8 pt-4 border-t border-bmlc-navy-50 px-1">
                  <div className="text-[11px] sm:text-sm text-gray-500">
                    {historyPagination.total_messages} total questions
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <button
                      onClick={() => fetchHistory(selectedUser.id, historyPage - 1)}
                      disabled={!historyPagination.has_prev}
                      className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full ${
                        historyPagination.has_prev
                          ? 'btn-secondary'
                          : 'bg-gray-100 text-gray-400 border border-transparent cursor-not-allowed'
                      }`}
                    >
                      Previous
                    </button>
                    <span className="text-[11px] sm:text-sm text-gray-500 px-2 whitespace-nowrap">
                      Page {historyPagination.current_page} of {historyPagination.total_pages}
                    </span>
                    <button
                      onClick={() => fetchHistory(selectedUser.id, historyPage + 1)}
                      disabled={!historyPagination.has_next}
                      className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full ${
                        historyPagination.has_next
                          ? 'btn-secondary'
                          : 'bg-gray-100 text-gray-400 border border-transparent cursor-not-allowed'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ---------- Activity list view ----------
  return (
    <div className="px-3 sm:px-4 py-4 sm:py-6 animate-fade-in">
      {/* Header */}
      <div className="page-hero mb-6 sm:mb-8">
        <div className="relative flex items-center gap-3 sm:gap-4">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white rounded-xl p-1 ring-1 ring-bmlc-gold-400/50 shadow-soft-sm flex-shrink-0 overflow-hidden">
            <img
              src="https://edifywebsite.blob.core.windows.net/edify-files/IMG_9812.JPG"
              alt="BM Law College Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-white">Chat Monitoring</h1>
            <p className="text-[11px] sm:text-sm text-bmlc-gold-300 font-medium tracking-wide">BM Law College Student Activity</p>
          </div>
        </div>
        <p className="relative mt-2.5 sm:mt-3 text-xs sm:text-sm text-bmlc-navy-100/90 max-w-2xl">
          Monitor which students are using the AI assistant and review their questions.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 sm:mb-6">
        {statCards.map((card) => (
          <div key={card.label} className="stat-card flex items-center gap-3 sm:gap-4 animate-slide-up">
            <div className={`w-11 h-11 sm:w-12 sm:h-12 ${card.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
              <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${card.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {card.icon}
              </svg>
            </div>
            <div className="min-w-0">
              <p className="stat-value">{card.value}</p>
              <p className="stat-label">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search Section */}
      <div className="card p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex items-center gap-2.5 mb-3 sm:mb-4">
          <div className="w-7 h-7 sm:w-8 sm:h-8 bg-bmlc-gold-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-bmlc-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h2 className="section-title pb-2 mb-0 text-base sm:text-lg">Search Students</h2>
        </div>

        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, email, or phone number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-premium"
            />
          </div>
          <button
            type="submit"
            className="btn-primary px-6 py-2.5 rounded-lg text-white font-semibold text-xs sm:text-sm whitespace-nowrap"
          >
            Search
          </button>
        </form>
      </div>

      {/* Activity Table */}
      <div className="card p-0 overflow-hidden">
        <div className="flex items-center justify-between p-4 sm:p-6 pb-3 sm:pb-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-bmlc-navy-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-bmlc-navy-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h2 className="section-title pb-2 mb-0 text-base sm:text-lg flex items-center gap-2">
              <span>Student Activity</span>
              {pagination && <span className="badge-navy font-sans">{pagination.total_users}</span>}
            </h2>
          </div>
          <button
            onClick={fetchActivity}
            className="btn-ghost p-2 sm:p-2.5 rounded-lg flex-shrink-0"
            title="Refresh"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="px-4 sm:px-6 pb-3 sm:pb-4">
            <div className="toast-error">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-xs sm:text-sm">{error}</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="px-4 sm:px-6 pb-6 pt-1 space-y-3" aria-busy="true">
            <div className="skeleton h-10 w-full rounded-xl"></div>
            <div className="skeleton h-12 w-full rounded-xl"></div>
            <div className="skeleton h-12 w-full rounded-xl"></div>
            <div className="skeleton h-12 w-full rounded-xl"></div>
            <p className="text-gray-400 text-xs sm:text-sm text-center pt-2">Loading activity...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center px-4 py-10 sm:py-14">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-bmlc-gold-50 ring-1 ring-bmlc-gold-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-bmlc-gold-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="font-serif text-bmlc-navy-800 text-base sm:text-lg font-semibold mb-1">No students found</p>
            <p className="text-gray-400 text-xs sm:text-sm">Try adjusting your search criteria</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table-premium min-w-full">
                <thead>
                  <tr>
                    <th className="text-left">
                      Student
                    </th>
                    <th className="text-left hidden sm:table-cell">
                      Phone
                    </th>
                    <th className="text-left">
                      Total Questions
                    </th>
                    <th className="text-left hidden md:table-cell">
                      Today
                    </th>
                    <th className="text-left hidden md:table-cell">
                      Last Active
                    </th>
                    <th className="text-left">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user.id}>
                      <td className="whitespace-nowrap">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-bmlc-gold-50 ring-2 ring-bmlc-gold-300/70 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] sm:text-xs font-serif font-bold text-bmlc-gold-700">
                              {user.username?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="text-xs sm:text-sm font-medium text-bmlc-navy-900 truncate max-w-[120px] sm:max-w-[200px]">
                            {user.username}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hidden sm:table-cell">
                        {user.phone_number || '-'}
                      </td>
                      <td className="whitespace-nowrap text-xs sm:text-sm">
                        <span className="font-serif font-bold text-bmlc-navy-900 text-sm sm:text-base">{user.total_messages}</span>
                      </td>
                      <td className="whitespace-nowrap text-xs sm:text-sm hidden md:table-cell">
                        {user.messages_today > 0 ? (
                          <span className="badge-gold">
                            {user.messages_today}
                          </span>
                        ) : (
                          <span className="text-gray-300 font-medium">0</span>
                        )}
                      </td>
                      <td className={`whitespace-nowrap text-xs sm:text-sm hidden md:table-cell ${user.last_message_at ? 'text-gray-600' : 'text-gray-300 italic'}`}>
                        {formatDate(user.last_message_at)}
                      </td>
                      <td className="whitespace-nowrap text-xs sm:text-sm">
                        <button
                          onClick={() => handleViewChats(user)}
                          className="btn-secondary inline-flex items-center gap-1.5 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-lg text-[10px] sm:text-xs font-semibold"
                        >
                          <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <span>View Chats</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t border-bmlc-navy-50 bg-bmlc-navy-50/40">
                <div className="text-[11px] sm:text-sm text-gray-500">
                  Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_users)} of{' '}
                  {pagination.total_users} students
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!pagination.has_prev}
                    className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full ${
                      pagination.has_prev
                        ? 'btn-secondary'
                        : 'bg-gray-100 text-gray-400 border border-transparent cursor-not-allowed'
                    }`}
                  >
                    Previous
                  </button>
                  <span className="text-[11px] sm:text-sm text-gray-500 px-2 whitespace-nowrap">
                    Page {pagination.current_page} of {pagination.total_pages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!pagination.has_next}
                    className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full ${
                      pagination.has_next
                        ? 'btn-secondary'
                        : 'bg-gray-100 text-gray-400 border border-transparent cursor-not-allowed'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatMonitoring;
